import { computed, Injectable, inject } from '@angular/core';

import dayjs from 'dayjs';
import weekday from 'dayjs/plugin/weekday';
import { timer } from 'rxjs';
import { DataService } from 'src/data-store/data.service';
import { IHotlineAvailabilityResult } from 'src/models/app.model';

dayjs.extend(weekday);

const WEEKDAY_PLACEHOLDER = '#';

export enum EHotlineAvailabilityKey {
  TODAY_FROM = 'global.hotline.todayFrom',
  TODAY_TO = 'global.hotline.todayTo',
  TOMORROW_FROM = 'global.hotline.tomorrowFrom',
  DYNAMIC_DAY_FROM = `global.hotline.dynamicDayFrom.${WEEKDAY_PLACEHOLDER}`,
}

/**
 * Instructions: https://www.notion.so/btech/MN-970-zobrazen-dostupnosti-telefonn-linky-28f645d0669c8022a3b0c6e9fe27d3dc
 */
@Injectable({
  providedIn: 'root'
})
export class HotlineAvailabilityService {
  private dataService = inject(DataService);

  private readonly HOURS_FORMAT = 'HH:mm';

  public hotlineAvailability = computed<IHotlineAvailabilityResult | null>(() => this.getHotlineAvailability());

  public constructor() {
    this.startHourlyFetch();
  }

  private startHourlyFetch(): void {
    const now = dayjs();
    const nextHour = now.add(1, 'hour').startOf('hour').add(5, 's');
    const msUntilNextHour = nextHour.diff(now);

    // Timer fires first at top of the next hour, then every hour
    timer(msUntilNextHour, 60 * 60 * 1000).subscribe(() => {
      this.dataService.getHotlineAvailability();
    });

    this.dataService.getHotlineAvailability();
  }

  private getHotlineAvailability(): IHotlineAvailabilityResult | null {
    const data = this.dataService.hotlineAvailability();

    if (!data) {
      return null;
    }

    const now = dayjs();
    const open = dayjs(data.openDateTime);
    const close = dayjs(data.closeDateTime);
    const isSameDay: boolean = open.isSame(now, 'day');
    const isNextDay: boolean = open.diff(now, 'day') === 1;

    const dayOfWeek: number = now.weekday();
    const targetDayOfWeek: number = open.weekday();

    if (data.openNow) {
      // Currently open
      return {
        open: true,
        translateKey: EHotlineAvailabilityKey.TODAY_TO,
        time: close.format(this.HOURS_FORMAT)
      };
    }

    if (now < open && isSameDay && !data.openNow) {
      // Before opening today
      return {
        open: false,
        translateKey: EHotlineAvailabilityKey.TODAY_FROM,
        time: open.format(this.HOURS_FORMAT)
      };
    }

    // After closing hours
    if (isNextDay && !data.openNow && dayOfWeek >= 0 && dayOfWeek <= 3) {
      // Monday–Thursday after closing → available tomorrow
      return {
        open: false,
        translateKey: EHotlineAvailabilityKey.TOMORROW_FROM,
        time: open.format(this.HOURS_FORMAT)
      };
    }

    // Friday-Sunday or holidays skipping
    return {
      open: false,
      translateKey: EHotlineAvailabilityKey.DYNAMIC_DAY_FROM.replace(WEEKDAY_PLACEHOLDER, targetDayOfWeek.toString()),
      time: open.format(this.HOURS_FORMAT)
    };
  }

}